'use client';

import * as React from 'react';
import { isAllowedCommentEmail } from 'lib/comment-email';
import { useTranslations } from 'next-intl';
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'ui';
import { useCommentMutation } from './comment-api';
import { COMMENT_INPUT } from './comment-editor';

export function CommentIdentity({
  postId,
  open,
  onOpenChange,
  manage = false,
}: {
  postId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  manage?: boolean;
}) {
  const t = useTranslations('Blog.Comments');
  const mutation = useCommentMutation(postId, manage);
  const [challengeId, setChallengeId] = React.useState<string>();
  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState<string>();
  const emailId = React.useId();
  const returnFocus = React.useRef<HTMLElement | null>(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        closeLabel={t('Close')}
        className="max-h-[90dvh] w-[calc(100%-2rem)] overflow-y-auto rounded-xl border-secondary bg-primary text-primary sm:max-w-sm"
        onOpenAutoFocus={() => {
          returnFocus.current = document.activeElement as HTMLElement | null;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          (returnFocus.current?.isConnected
            ? returnFocus.current
            : document.getElementById(`comment-toggle-${postId}`)
          )?.focus();
        }}
      >
        <DialogHeader className="mb-1 pe-6">
          <DialogTitle>
            {challengeId ? t('CheckInbox') : t('SignIn')}
          </DialogTitle>
          <DialogDescription
            className={!challengeId ? 'sr-only' : 'break-words pt-2 text-xs'}
          >
            {challengeId ? email : t('Email')}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={async (event) => {
            event.preventDefault();
            setError(undefined);
            if (!challengeId && !isAllowedCommentEmail(email)) {
              setError('email-not-allowed');
              return;
            }
            try {
              if (challengeId) {
                await mutation.mutateAsync({
                  action: 'verify-code',
                  challengeId,
                  email: email.trim().toLowerCase(),
                  code,
                });
                setChallengeId(undefined);
                setCode('');
                onOpenChange(false);
              } else {
                const result = await mutation.mutateAsync({
                  action: 'request-code',
                  email: email.trim().toLowerCase(),
                });
                setChallengeId(result.challengeId);
              }
            } catch {
              /* Keep the form on failure. */
            }
          }}
        >
          {challengeId ? (
            <input
              key={challengeId}
              className={cn(
                COMMENT_INPUT,
                'h-14 text-center font-mono text-2xl tracking-[0.35em] tabular-nums',
              )}
              aria-label={t('VerificationCode')}
              required
              inputMode="numeric"
              autoComplete="one-time-code"
              disabled={mutation.isPending}
              pattern="[0-9]{6}"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(event) => {
                setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                mutation.reset();
              }}
              autoFocus
            />
          ) : (
            <div className="space-y-2">
              <label htmlFor={emailId} className="text-sm text-secondary">
                {t('Email')}
              </label>
              <input
                id={emailId}
                type="email"
                className={`${COMMENT_INPUT} h-11`}
                required
                maxLength={254}
                autoComplete="email"
                disabled={mutation.isPending}
                placeholder="you@gmail.com"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setError(undefined);
                  mutation.reset();
                }}
              />
            </div>
          )}
          <Button
            type="submit"
            className="h-11 w-full motion-safe:active:scale-[0.96]"
            disabled={
              mutation.isPending ||
              (challengeId ? code.length !== 6 : !email.trim())
            }
          >
            {mutation.isPending
              ? t('Sending')
              : challengeId
                ? t('Verify')
                : t('Continue')}
          </Button>
          {challengeId && (
            <button
              type="button"
              className="w-full rounded py-1 text-sm text-tertiary hover:text-primary"
              disabled={mutation.isPending}
              onClick={() => {
                setChallengeId(undefined);
                setCode('');
                mutation.reset();
              }}
            >
              {t('ChangeEmail')}
            </button>
          )}
        </form>
        {(error || mutation.error) && (
          <CommentErrorMessage code={error || mutation.error!.message} />
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CommentErrorMessage({ code }: { code: string }) {
  const t = useTranslations('Blog.Comments');
  const key = (
    {
      'rate-limited': 'RateLimited',
      'invalid-code': 'InvalidCode',
      'email-unavailable': 'EmailUnavailable',
      'email-not-allowed': 'EmailNotAllowed',
      'invalid-avatar': 'InvalidAvatar',
      'avatar-too-large': 'AvatarTooLarge',
      'comments-disabled': 'Disabled',
      'password-required': 'PasswordRequired',
      'sign-in-required': 'SignInToComment',
      forbidden: 'Forbidden',
      'comment-not-found': 'CommentGone',
      'invalid-parent': 'CommentGone',
    } as const
  )[code];
  return (
    <p role="alert" className="text-sm text-error-primary">
      {t(key ?? 'Error')}
    </p>
  );
}
